import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  UnitsDescriptionRead,
  UnitDescriptionPayload,
  UnitsDescription,
} from "@types";
import {
  createUnitDescription,
  deleteUnitDescription,
  getAllUnitDescriptions,
  getUnitsDescriptionByLocation,
  updateUnitDescription,
} from "@/api";
import { useInfiniteList } from "@hooks";

export const UNIT_DESCRIPTIONS_QK = ["unit-descriptions"] as const;

export function useAllUnitDescription(withPagination: boolean = true) {
  return useInfiniteList<UnitsDescriptionRead>({
    queryKey: [...UNIT_DESCRIPTIONS_QK, withPagination],
    fetchFn: async (page: number) => {
      const res = await getAllUnitDescriptions(
        withPagination ? page : undefined,
        withPagination,
      );
      return res.data;
    },
  });
}
export function useUnitsDescriptionByLocation(
  locationId: string | null,
  limit = 10,
) {
  return useInfiniteList<UnitsDescriptionRead>({
    queryKey: ["unitsDescriptionByLocation", locationId, limit],
    fetchFn: async (page) => {
      if (!locationId) {
        return {
          data: [],
          totalCount: 0,
          totalPages: 1,
        };
      }

      const res = await getUnitsDescriptionByLocation(locationId, page, limit);

      return {
        data: res.data.data,
        totalCount: res.data.totalCount,
        totalPages: res.data.totalPages,
      };
    },
  });
}

export function useCreateUnitDescriptions() {
  const queryClient = useQueryClient();

  const { mutateAsync: triggerCreateUnitDescription, isPending: loading } =
    useMutation<UnitsDescriptionRead, Error, UnitsDescription>({
      mutationFn: createUnitDescription,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: UNIT_DESCRIPTIONS_QK });
      },
    });

  return { triggerCreateUnitDescription, loading };
}

export function useUpdateUnitDescriptions() {
  const queryClient = useQueryClient();

  type UpdateArgs = { id: string; payload: UnitDescriptionPayload };

  const { mutateAsync: triggerUpdateUnitDescription, isPending: loading } =
    useMutation<UnitsDescriptionRead, Error, UpdateArgs>({
      mutationFn: ({ id, payload }) => updateUnitDescription(id, payload),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: UNIT_DESCRIPTIONS_QK });
        queryClient.invalidateQueries({ queryKey: ["units"] });
      },
    });

  return { triggerUpdateUnitDescription, loading };
}

export function useRemoveUnitDescriptionById() {
  const queryClient = useQueryClient();

  const { mutateAsync: removeUnitDescription, isPending: loading } =
    useMutation<UnitsDescriptionRead, Error, string>({
      mutationFn: deleteUnitDescription,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: UNIT_DESCRIPTIONS_QK });
      },
      onError: () => {
        console.error("Error deleting the unit description");
      },
    });

  return { removeUnitDescription, loading };
}
